import React, { useState } from 'react';
import { X, FileText } from 'lucide-react';
import * as xlsx from 'xlsx';
import { importClients } from '../../services/adminService';

const TARGET_FIELDS = [
    { key: 'email', label: 'Email' },
    { key: 'Nombre', label: 'Nombre' },
    { key: 'Apellido', label: 'Apellido' },
    { key: 'Telefono', label: 'Teléfono' },
    { key: 'Compania', label: 'Compañía' },
    { key: 'Estado', label: 'Estado' }
];

const BulkClientUpload = ({ onClose, onDone }) => {
    const [file, setFile] = useState(null);
    const [columns, setColumns] = useState([]);
    const [rows, setRows] = useState([]);
    const [mapping, setMapping] = useState({});
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const handleFile = async (f) => {
        setError(null);
        setResult(null);
        setFile(f);
        try {
            const data = await f.arrayBuffer();
            const wb = xlsx.read(data, { type: 'array' });
            const sheet = wb.Sheets[wb.SheetNames[0]];
            const json = xlsx.utils.sheet_to_json(sheet, { defval: '' });
            setRows(json);
            const cols = json.length > 0 ? Object.keys(json[0]) : [];
            setColumns(cols);

            // Auto-mapping based on heuristics
            const auto = {};
            cols.forEach(c => {
                const key = c.toString().toLowerCase();
                if (key.includes('email')) auto.email = c;
                else if (key.includes('name') && !auto.Nombre) auto.Nombre = c;
                else if (key.includes('apellido') || key.includes('last')) auto.Apellido = c;
                else if (key.includes('phone') || key.includes('tel')) auto.Telefono = c;
                else if (key.includes('company') || key.includes('compania')) auto.Compania = c;
                else if (key.includes('estado') || key.includes('status')) auto.Estado = c;
            });
            setMapping(auto);
        } catch (err) {
            console.error(err);
            setError('No se pudo leer el archivo. Use un XLSX/CSV válido.');
        }
    };

    const handleImport = async () => {
        if (!file) return setError('Seleccione un archivo primero');
        // Require a mapped email column for better UX
        if (!mapping.email) return setError('No se detectó la columna de Email. Mapea la columna que contiene emails.');
        setLoading(true);
        setError(null);
        try {
            console.log('BulkClientUpload: uploading file', { name: file.name, size: file.size, type: file.type });
            const res = await importClients(file);
            setResult(res);
            if (onDone) onDone(res);
        } catch (err) {
            console.error('BulkClientUpload import error:', err);
            setError(err.message || JSON.stringify(err) || 'Error durante la importación');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl overflow-hidden">
                <div className="p-4 border-b flex justify-between items-center">
                    <h3 className="text-lg font-bold">Importar Clientes (XLSX)</h3>
                    <button onClick={onClose} className="text-slate-500 hover:text-slate-700"><X size={20} /></button>
                </div>
                <div className="p-6 space-y-4">
                    <div>
                        <input
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            onChange={e => { if (e.target.files && e.target.files[0]) handleFile(e.target.files[0]); }}
                            className=""
                        />
                        <p className="text-xs text-slate-400 mt-1">Columnas esperadas: Email, Nombre, Apellido, Telefono, Compania, Estado. Los duplicados por email serán saltados.</p>
                    </div>

                    {columns.length > 0 && (
                        <div className="space-y-3">
                            <h4 className="font-medium">Mapeo de columnas</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {TARGET_FIELDS.map(field => (
                                    <div key={field.key} className="flex items-center gap-3">
                                        <label className="w-28 text-sm text-slate-700">{field.label}</label>
                                        <select
                                            value={mapping[field.key] || ''}
                                            onChange={e => setMapping({...mapping, [field.key]: e.target.value})}
                                            className="flex-1 px-3 py-2 border rounded"
                                        >
                                            <option value="">- No mapear -</option>
                                            {columns.map(c => (
                                                <option key={c} value={c}>{c}</option>
                                            ))}
                                        </select>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {rows.length > 0 && (
                        <div>
                            <h4 className="font-medium mb-2">Vista previa (primeras filas)</h4>
                            <div className="overflow-x-auto max-h-56">
                                <table className="w-full text-sm text-left border-collapse">
                                    <thead>
                                        <tr className="border-b text-slate-500">
                                            {TARGET_FIELDS.map(f => (
                                                <th className="p-2" key={f.key}>{f.label}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.slice(0, 6).map((r, idx) => (
                                            <tr key={idx} className="border-b even:bg-slate-50">
                                                {TARGET_FIELDS.map(f => (
                                                    <td className="p-2" key={f.key}>{r[mapping[f.key]] ?? ''}</td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {error && <div className="text-sm text-red-600">{error}</div>}
                    {result && (
                        <div className="bg-emerald-50 border border-emerald-100 p-3 rounded text-sm">
                            <div>Insertados: <strong>{result.inserted}</strong></div>
                            <div>Saltados: <strong>{result.skipped}</strong></div>
                            {result.errors && result.errors.length > 0 && (
                                <div className="mt-2">
                                    <div className="font-medium">Errores:</div>
                                    <ul className="list-disc ml-5 text-xs text-slate-700">
                                        {result.errors.map((e, i) => (<li key={i}>Fila {e.row}: {e.message}</li>))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="p-4 border-t flex justify-end gap-3 bg-slate-50">
                    <button onClick={onClose} className="px-4 py-2 rounded bg-white border">Cancelar</button>
                    <button
                        onClick={handleImport}
                        disabled={loading || !file}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                        {loading ? 'Importando...' : (<><FileText size={16} /> Importar</>)}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BulkClientUpload;
